<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="receipt-payment-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">Receipt &amp; Payment Statement</h1>
          <p class="page-subtitle">Summary of all receipts and payments for the selected period</p>
        </div>
        <div class="header-actions">
          <IntercomButton variant="secondary" @click="exportForm10BDReport" :loading="isExporting10BD">
            Form 10BD Export
          </IntercomButton>
          <IntercomButton variant="secondary" @click="exportCSV">
            Export CSV
          </IntercomButton>
          <IntercomButton variant="secondary" @click="handlePrint">
            Print
          </IntercomButton>
        </div>
      </div>

      <!-- Filter Bar -->
      <div class="filters-row no-print">
        <div class="filter-field">
          <IntercomDateInput
            v-model="startDate"
            label="Start Date"
            class="date-input"
          />
        </div>
        <div class="filter-field">
          <IntercomDateInput
            v-model="endDate"
            label="End Date"
            class="date-input"
          />
        </div>
        <div class="filter-field">
          <label class="filter-label">Bank Account</label>
          <IntercomSelect
            v-model="selectedAccountId"
            :options="accountOptions"
            placeholder="All Accounts"
            class="account-select"
          />
        </div>
        <div class="filter-actions">
          <IntercomButton variant="primary" :loading="isLoading" @click="generateReport">
            Generate
          </IntercomButton>
        </div>
      </div>

      <!-- Report Table -->
      <IntercomCard class="report-card">
        <IntercomTable
          :columns="columns"
          :data="rows"
          :loading="isLoading"
          hoverable
        >
          <template #cell-date="{ value }">
            {{ formatDate(value) }}
          </template>
          <template #cell-entry_type="{ value }">
            <IntercomBadge :variant="typeBadgeVariant(value)">
              {{ typeLabel(value) }}
            </IntercomBadge>
          </template>
          <template #cell-amount_in="{ value }">
            <span v-if="value > 0" class="amount-in">
              {{ formatFullCurrency(value) }}
            </span>
          </template>
          <template #cell-amount_out="{ value }">
            <span v-if="value > 0" class="amount-out">
              {{ formatFullCurrency(value) }}
            </span>
          </template>
        </IntercomTable>

        <p v-if="!isLoading && !hasGenerated" class="empty-text">
          Select a date range and click Generate to view the report.
        </p>
        <p v-if="!isLoading && hasGenerated && rows.length === 0" class="empty-text">
          No transactions found for the selected period.
        </p>

        <!-- Summary Row -->
        <div v-if="hasGenerated && rows.length > 0" class="summary-row">
          <div class="summary-spacer" />
          <div class="summary-totals">
            <div class="summary-item">
              <span class="summary-label">Total Receipts</span>
              <span class="summary-value amount-in">{{ formatFullCurrency(totalReceipts) }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Total Payments</span>
              <span class="summary-value amount-out">{{ formatFullCurrency(totalPayments) }}</span>
            </div>
            <div class="summary-item summary-net">
              <span class="summary-label">Net</span>
              <span class="summary-value" :class="netAmount >= 0 ? 'amount-in' : 'amount-out'">
                {{ formatFullCurrency(netAmount) }}
              </span>
            </div>
          </div>
        </div>
      </IntercomCard>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency, formatFullCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomInput,
  IntercomDateInput,
  IntercomSelect,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listBankAccounts, getReceiptPaymentStatement, exportForm10BD } = useSeva()
const { navSections } = useNavigation()

// State
const startDate = ref(dayjs().startOf('month').format('YYYY-MM-DD'))
const endDate = ref(dayjs().format('YYYY-MM-DD'))
const selectedAccountId = ref(null)
const bankAccounts = ref([])
const rows = ref([])
const isLoading = ref(false)
const isExporting10BD = ref(false)
const hasGenerated = ref(false)

// Helpers
const formatDate = (date) => date ? dayjs(date).format('DD/MM/YYYY') : ''

const typeBadgeVariant = (type) => {
  const map = {
    donation: 'success',
    expense: 'error',
    transfer_in: 'info',
    transfer_out: 'warning',
  }
  return map[type] || 'default'
}

const typeLabel = (type) => {
  const map = {
    donation: 'Donation',
    expense: 'Expense',
    transfer_in: 'Transfer In',
    transfer_out: 'Transfer Out',
  }
  return map[type] || type
}

// Computed
const accountOptions = computed(() => [
  { value: null, label: 'All Accounts' },
  ...bankAccounts.value.map(a => ({ value: a.id, label: a.name })),
])

const totalReceipts = computed(() =>
  rows.value.reduce((sum, r) => sum + (r.amount_in || 0), 0)
)

const totalPayments = computed(() =>
  rows.value.reduce((sum, r) => sum + (r.amount_out || 0), 0)
)

const netAmount = computed(() => totalReceipts.value - totalPayments.value)

// Table columns
const columns = [
  { key: 'date', title: 'Date', width: '110px' },
  { key: 'entry_type', title: 'Type', width: '130px' },
  { key: 'description', title: 'Description' },
  { key: 'reference', title: 'Reference', width: '130px' },
  { key: 'account_name', title: 'Account', width: '150px' },
  { key: 'amount_in', title: 'Receipt (In)', width: '130px', align: 'right' },
  { key: 'amount_out', title: 'Payment (Out)', width: '130px', align: 'right' },
]

// API
const fetchBankAccounts = async () => {
  try {
    const response = await listBankAccounts()
    bankAccounts.value = response?.accounts || response?.bank_accounts || []
  } catch (error) {
    console.error('Failed to fetch bank accounts:', error)
  }
}

const generateReport = async () => {
  if (!startDate.value || !endDate.value) {
    toast.warning('Please select a start date and end date.')
    return
  }
  isLoading.value = true
  try {
    const params = {
      start_date: startDate.value,
      end_date: endDate.value,
    }
    if (selectedAccountId.value) {
      params.bank_account_id = selectedAccountId.value
    }
    const response = await getReceiptPaymentStatement(params)
    rows.value = response?.rows || []
    hasGenerated.value = true
  } catch (error) {
    console.error('Failed to generate report:', error)
    toast.error(error.detail || error.message || 'Failed to load report.')
  } finally {
    isLoading.value = false
  }
}

const handlePrint = () => {
  window.print()
}

const exportCSV = () => {
  if (!hasGenerated.value || rows.value.length === 0) {
    toast.warning('Generate the report first before exporting.')
    return
  }

  const headers = ['Date', 'Type', 'Description', 'Reference', 'Account', 'Receipt (In)', 'Payment (Out)']
  const csvRows = rows.value.map(r => [
    formatDate(r.date),
    typeLabel(r.entry_type),
    `"${(r.description || '').replace(/"/g, '""')}"`,
    r.reference || '',
    r.account_name || '',
    r.amount_in > 0 ? r.amount_in.toFixed(2) : '',
    r.amount_out > 0 ? r.amount_out.toFixed(2) : '',
  ])
  csvRows.push([
    '', '', '', '', 'Totals',
    totalReceipts.value.toFixed(2),
    totalPayments.value.toFixed(2),
  ])

  const csv = [headers.join(','), ...csvRows.map(r => r.join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `receipt-payment-${startDate.value}-to-${endDate.value}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

// Form 10BD export — fiscal year derived from the selected start date
const exportForm10BDReport = async () => {
  // Derive fiscal year from startDate (e.g., 2025-04-01 → "2025-26", 2026-01-15 → "2025-26")
  const d = dayjs(startDate.value)
  const month = d.month()
  const year = d.year()
  const fyStart = month >= 3 ? year : year - 1
  const fiscalYear = `${fyStart}-${String(fyStart + 1).slice(-2)}`

  isExporting10BD.value = true
  try {
    const blob = await exportForm10BD(fiscalYear)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `form-10bd-FY${fiscalYear}.csv`
    link.click()
    URL.revokeObjectURL(url)
    toast.success(`Form 10BD exported for FY ${fiscalYear}`)
  } catch (error) {
    console.error('Failed to export Form 10BD:', error)
    toast.error(error.detail || error.message || 'Failed to export Form 10BD')
  } finally {
    isExporting10BD.value = false
  }
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(async () => {
  fetchBusiness()
  await fetchBankAccounts()
  // Auto-generate for current month so the page isn't empty on load
  generateReport()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.receipt-payment-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.header-actions {
  display: flex;
  gap: var(--space-3);
}

.filters-row {
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.filter-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.date-input {
  width: 160px;
}

.account-select {
  width: 200px;
}

.filter-actions {
  padding-bottom: 2px;
}

.report-card {
  overflow: visible;
}

.amount-in {
  color: var(--color-green-600, #16a34a);
  font-weight: var(--font-weight-medium);
}

.amount-out {
  color: var(--color-red-600, #dc2626);
  font-weight: var(--font-weight-medium);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

.summary-row {
  display: flex;
  justify-content: flex-end;
  border-top: 2px solid var(--color-gray-200);
  padding: var(--space-4) var(--space-4) var(--space-2);
  margin-top: var(--space-2);
}

.summary-spacer {
  flex: 1;
}

.summary-totals {
  display: flex;
  gap: var(--space-8);
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-1);
}

.summary-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.summary-value {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}

.summary-net {
  padding-left: var(--space-6);
  border-left: 1px solid var(--color-gray-200);
}

@media (max-width: 768px) {
  .receipt-payment-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .header-actions {
    flex-direction: column;
    width: 100%;
  }

  .filters-row {
    flex-direction: column;
    align-items: stretch;
  }

  .date-input,
  .account-select {
    width: 100%;
  }

  .summary-totals {
    flex-direction: column;
    gap: var(--space-3);
    align-items: flex-end;
  }

  .summary-net {
    padding-left: 0;
    border-left: none;
    border-top: 1px solid var(--color-gray-200);
    padding-top: var(--space-3);
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  .page-header .header-actions {
    display: none !important;
  }

  .receipt-payment-view {
    padding: 0;
  }

  .report-card {
    box-shadow: none;
    border: none;
  }
}
</style>
